import React, { useContext } from "react";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import {
  PrimaryButton,
  CancelButton,
} from "components/Common/CustomButtons/CustomButtons";
import { GlobalContext } from "contexts/GlobalContext";
import "./VolCalcFailedModal.scss";

/**
 * Shared popup for the "couldn't calculate volatility" backend response
 * (`vol_calculation_failed`). Used by both Trade Capture and the Pricer CSV
 * import. When `canRetry` is true it offers a confirm action that re-sends the
 * import with `skip_vol_calculation`; otherwise it only acknowledges (the user
 * must fix the source data and re-upload).
 */
const VolCalcFailedModal = ({
  open,
  info,
  canRetry = true,
  loading = false,
  onRetry,
  onCancel,
  title = "Volatility Calculation Failed",
  retryLabel = "Import anyway",
  cancelLabel = "Cancel",
  acknowledgeLabel = "OK",
  description = null,
  showOptionList = true,
}) => {
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);

  const failedOptions = Array.isArray(info?.failedOptions)
    ? info.failedOptions
    : [];
  const failedDetail = Array.isArray(info?.detail) ? info.detail : [];

  const reasonLabel = (reason) =>
    reason === "expired" ? "tenor expired" : "can't be priced";

  const footer = canRetry
    ? [
        <CancelButton key="cancel" onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </CancelButton>,
        <PrimaryButton key="confirm" onClick={onRetry} loading={loading}>
          {retryLabel}
        </PrimaryButton>,
      ]
    : [
        <PrimaryButton key="ok" onClick={onCancel}>
          {acknowledgeLabel}
        </PrimaryButton>,
      ];

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined
            style={{ color: "#faad14", marginRight: 8 }}
          />
          {title}
        </span>
      }
      open={open}
      onCancel={onCancel}
      maskClosable={!loading}
      closable={!loading}
      keyboard={!loading}
      getContainer={"#content-root"}
      destroyOnClose
      className={`vol-calc-failed-modal ${
        dark_Theme ? "ant-modal-dark content_dark" : "ant-modal-light"
      }`}
      footer={footer}
    >
      <p style={{ whiteSpace: "pre-line", marginBottom: 12 }}>
        {info?.message}
      </p>

      {showOptionList && failedDetail.length ? (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {failedDetail.map((o) => (
            <li key={o.option_number}>
              <strong>#{o.option_number}</strong>
              {o.context ? ` ${o.context}` : ""} —{" "}
              <em>{reasonLabel(o.reason)}</em>
            </li>
          ))}
        </ul>
      ) : showOptionList && failedOptions.length ? (
        <div>
          <strong>
            Affected option{failedOptions.length > 1 ? "s" : ""}:
          </strong>{" "}
          {failedOptions.map((n) => `#${n}`).join(", ")}
        </div>
      ) : null}

      {description ? (
        <div style={{ marginTop: 12 }}>{description}</div>
      ) : null}
    </Modal>
  );
};

VolCalcFailedModal.propTypes = {
  open: PropTypes.bool,
  info: PropTypes.shape({
    message: PropTypes.string,
    failedOptions: PropTypes.arrayOf(PropTypes.number),
    detail: PropTypes.arrayOf(
      PropTypes.shape({
        option_number: PropTypes.number,
        context: PropTypes.string,
        reason: PropTypes.string,
      })
    ),
  }),
  canRetry: PropTypes.bool,
  loading: PropTypes.bool,
  onRetry: PropTypes.func,
  onCancel: PropTypes.func,
  title: PropTypes.string,
  retryLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  acknowledgeLabel: PropTypes.string,
  description: PropTypes.node,
  showOptionList: PropTypes.bool,
};

export default VolCalcFailedModal;
